import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "@/firebase/config";

/**
 * Upload image to Firebase Storage and return the download URL
 * @param file - The image file to upload
 * @param folder - The folder path in storage (default: "newsletter-images")
 * @returns Promise<string> - The download URL of the uploaded image
 */
export const uploadImageToFirebase = async (
  file: File,
  folder: string = "newsletter-images"
): Promise<string> => {
  try {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      throw new Error("File must be an image");
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error("Image size must be less than 5MB");
    }

    // Create unique filename with timestamp
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name.replace(
      /[^a-zA-Z0-9.-]/g,
      "_"
    )}`;
    const imagePath = `${folder}/${fileName}`;

    // Create storage reference
    const storageRef = ref(storage, imagePath);

    // Upload file
    console.log("📤 Uploading image to Firebase Storage:", fileName);
    const snapshot = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log("✅ Image uploaded successfully:", downloadURL);

    return downloadURL;
  } catch (error) {
    console.error("❌ Error uploading image:", error);
    throw error;
  }
};

/**
 * Convert base64 data URL to File object
 * @param dataUrl - Base64 data URL
 * @param filename - Filename for the file
 * @returns File object
 */
export const dataURLtoFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

/**
 * Usable content width inside the newsletter email column.
 * Template container is max-width 600px with 20px container padding
 * and 20px content side-padding: 600 - 40 - 40 = 520px.
 */
const MAX_EMAIL_IMAGE_WIDTH = 520;

/**
 * Measure an image's intrinsic (natural) width without rendering it into the
 * page. Resolves to null if the image can't be loaded/measured, or if it does
 * not load within timeoutMs (so a stalled remote URL can never hang the caller).
 * @param src - Image src (data URL or remote URL)
 * @param timeoutMs - Max time to wait for the image to load (default 4000ms)
 * @returns Promise<number | null> - natural width in px, or null
 */
const getNaturalWidth = (
  src: string,
  timeoutMs = 4000
): Promise<number | null> =>
  new Promise((resolve) => {
    const probe = new Image();
    const done = (value: number | null) => {
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    probe.onload = () => done(probe.naturalWidth || null);
    probe.onerror = () => done(null);
    probe.src = src;
  });

/**
 * Process HTML content and upload all base64 images to Firebase
 * @param html - HTML content with potential base64 images
 * @param folder - Storage folder path
 * @returns Promise<string> - HTML with Firebase URLs
 */
export const processImagesInHtml = async (
  html: string,
  folder: string = "newsletter-images"
): Promise<string> => {
  try {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const images = doc.querySelectorAll("img");
    
    console.log(`🔍 Scanning ${images.length} images in content...`);

    const tasks = Array.from(images).map(async (img, index) => {
      const src = img.getAttribute("src");

      // Cap the width to the email column, but never upscale a smaller image
      // (which would blur logos/icons/thumbnails). Measure from the current
      // src — for base64 this is instant; for remote URLs it's a quick load.
      const naturalWidth = src ? await getNaturalWidth(src) : null;
      const cap = Math.min(naturalWidth ?? MAX_EMAIL_IMAGE_WIDTH, MAX_EMAIL_IMAGE_WIDTH);

      // Outlook (Word engine) ignores max-width and only honors the width
      // attribute; every other client honors the CSS. width:100% + max-width
      // keeps it responsive on phones; height:auto preserves aspect ratio.
      img.setAttribute("width", String(cap));
      img.style.width = "100%";
      img.style.maxWidth = `${cap}px`;
      img.style.height = "auto";
      img.style.display = "block";
      // Auto horizontal margins center the block image when it's narrower
      // than the email body. text-align on a container won't center a
      // display:block image, so the centering has to live on the img itself.
      img.style.marginLeft = "auto";
      img.style.marginRight = "auto";

      // Handle base64 images
      if (src && src.startsWith("data:image/")) {
        console.log(`Processing inline base64 image ${index}...`);
        try {
          const filename = `newsletter_img_${Date.now()}_${index}.png`;
          const file = dataURLtoFile(src, filename);
          const firebaseUrl = await uploadImageToFirebase(file, folder);

          img.setAttribute("src", firebaseUrl);
          // Add alt text if missing for better deliverability
          if (!img.getAttribute("alt")) {
            img.setAttribute("alt", "Newsletter Image");
          }
          console.log(`✅ Converted base64 image to: ${firebaseUrl}`);
        } catch (error) {
          console.error(`❌ Failed to upload inline image ${index}:`, error);
        }
      } else if (src) {
        // Log existing URLs for debugging
        console.log(`Existing image URL: ${src.substring(0, 50)}...`);
      }
    });

    await Promise.all(tasks);

    // Return the processed HTML
    // We use body.innerHTML to get the content of the body, omitting html/head/body tags
    const processedHtml = doc.body.innerHTML;
    
    return processedHtml;
  } catch (error) {
    console.error("❌ Error processing images in HTML:", error);
    // Return original HTML as fallback
    return html;
  }
};

